export const MONTH_NAMES = ['Janv', 'Fév', 'Mars', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sept', 'Oct', 'Nov', 'Déc'];

export type RangeKey = '1d' | '3d' | '7d' | '30d';

export const rangeToDays: Record<RangeKey, number> = {
  '1d': 1,
  '3d': 3,
  '7d': 7,
  '30d': 30,
};

export const rangePresets: { key: RangeKey; label: string }[] = [
  { key: '1d', label: '1j' },
  { key: '3d', label: '3j' },
  { key: '7d', label: '7j' },
  { key: '30d', label: '30j' },
];

export const formatRangeDate = (date: Date): string =>
  date.toLocaleDateString('fr-FR', { month: 'short', day: 'numeric' });

export const formatInputDate = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/**
 * Pack time-interval items into the fewest possible lanes (greedy interval partitioning).
 * Items are sorted by start; each one goes into the lowest-index lane whose last item has
 * already ended, so non-overlapping items keep sharing the first lane and only overlaps
 * spill onto a new one. A null start counts as -∞, a null end as +∞.
 *
 * `minDurationMs` is a minimum on-screen footprint so bars (or their overflowing labels)
 * don't visually overlap: an item effectively occupies at least `[start, start + min]`,
 * pushing a too-close neighbour onto the next lane. Pass a function for a per-item
 * footprint (e.g. derived from each item's label width).
 */
export function packLanes<T extends { start: Date | null; end: Date | null }>(
  items: T[],
  minDurationMs: number | ((item: T) => number) = 0,
): T[][] {
  const minOf = typeof minDurationMs === 'function' ? minDurationMs : () => minDurationMs;
  const startMs = (i: T) => (i.start ? i.start.getTime() : -Infinity);
  const endMs = (i: T) => {
    const end = i.end ? i.end.getTime() : Infinity;
    return Math.max(end, startMs(i) + minOf(i));
  };
  const sorted = [...items].sort((a, b) => startMs(a) - startMs(b));
  const lanes: { end: number; items: T[] }[] = [];
  for (const item of sorted) {
    const lane = lanes.find((l) => l.end <= startMs(item));
    if (lane) {
      lane.items.push(item);
      lane.end = endMs(item);
    } else {
      lanes.push({ end: endMs(item), items: [item] });
    }
  }
  return lanes.map((l) => l.items);
}
